import { BreakpointObserver } from '@angular/cdk/layout'
import {
    HttpClient,
    HttpContext,
    HttpErrorResponse,
    HttpHeaders,
} from '@angular/common/http'
import { Injectable, Injector } from '@angular/core'
import { MatSnackBar } from '@angular/material/snack-bar'
import { Store } from '@ngrx/store'
import { EMPTY, Observable, throwError, timer } from 'rxjs'
import { catchError, mergeMap, retryWhen, tap, timeout } from 'rxjs/operators'
import { environment } from 'src/environments/environment'
import { AccommodationType, AppState, ReviewPage, SaleType } from '../ngrx-store/app.state'
import { postRestored, profileUpdateSuccess, savedAccommodationsLoaded, savedSalesLoaded } from '../ngrx-store/actions/corp-member.actions'
import { chatOutboxCleared } from '../ngrx-store/actions/chat-outbox.actions'
import { MESSAGES_CHAT_QUERY_PARAM, NATIONAL_CHAT_URL_VALUE, URLPaths } from '../utils/constants.util'
import { RETURN_URL_QUERY_PARAM, safeReturnUrl } from '../utils/return-url.util'
import { ONBOARDING_SNOOZE_HOURS, snoozeMessage } from '../utils/onboarding.util'
import { BaseService } from './base.service'
import { PublicStats } from '../home-page/learn-more-dialog/learn-more.content'
import { CACHE_TTL_MS } from '../http-interceptor/cache.interceptor'
import { MatDialog } from '@angular/material/dialog'
import { MatBottomSheet } from '@angular/material/bottom-sheet'
import { Router } from '@angular/router'
import { SocketIoService } from './socket-io.service'
import { SocketIoChatNamespaceService } from './socket-io.chat-ns.service'
import { PushNotificationService } from './push-notification.service'
import { PpaDirectoryService } from './ppa-directory.service'

/**
 * Once a call returns 401 logout the user; using interceptor!
 */

@Injectable({
    providedIn: 'root',
})
export class CallerService extends BaseService {
    constructor(
        private http: HttpClient,
        snackBar: MatSnackBar,
        breakpointObserver: BreakpointObserver,
        store: Store<AppState>,
        dialog: MatDialog,
        bottomSheet: MatBottomSheet,
        private router: Router,
        private socketIoService: SocketIoService,
        private socketIoChatNamespaceService: SocketIoChatNamespaceService,
        private injector: Injector,
    ) {
        super(snackBar, dialog, bottomSheet, breakpointObserver, store)
    }

    private HAS_AUTH = Boolean(sessionStorage.getItem(CallerService.SESSION_STORAGE_DATA_KEY))
    private AUTH_HEADER_VALUE: string = this.HAS_AUTH ? 'Bearer ' + sessionStorage.getItem(CallerService.SESSION_STORAGE_DATA_KEY) : ''

    private JSONHttpOptions = {
        headers: new HttpHeaders({
            'Content-Type': 'application/json',
            ...(this.HAS_AUTH) && { Authorization: this.AUTH_HEADER_VALUE }
        }),
        observe: 'response' as const,
        responseType: 'json' as const,
        withCredentials: true,
    }

    private TextHttpOptions = {
        headers: new HttpHeaders({
            'Content-Type': 'application/text',
            ...(this.HAS_AUTH) && { Authorization: this.AUTH_HEADER_VALUE }
        }),
        observe: 'response' as const,
        responseType: 'text' as const,
        withCredentials: true,
    }

    private FormDataOptions = {
        headers: new HttpHeaders({
            // exclude content-type because we'd need to set boundary, and the browser might choose a different boundary, causing it to fail on the backend. Also, the browser sets it for us if we leave it.
            ...(this.HAS_AUTH) && { Authorization: this.AUTH_HEADER_VALUE }
        }),
        observe: 'response' as const,
        responseType: 'text' as const,
        withCredentials: true,
    }

    // should this be commonly available ?
    private handleError(error: HttpErrorResponse) {
        console.error('got this error', error)
        // Report to sentry maybe?
        if (error.error instanceof ErrorEvent) {
            // A client-side or network error occurred. Handle it accordingly.
            console.error('An error occurred:', error.error.message)
        } else {
            // The backend returned an unsuccessful response code.
            // The response body may contain clues as to what went wrong.
            console.error(error)

            console.error(
                `Backend returned code ${error.status}, ` +
                    `body was: ${error.error}`,
            )
        }
        // Return an observable with a user-facing error message.

        return throwError(error)
    }

    sayHi() {
        return this.http.get(
            environment.baseurl + URLPaths.sayHi,
            this.JSONHttpOptions
        )
    }

    /**
     * Opens both sockets' transport connection using the current corpMember identity. Idempotent
     * (connect() no-ops if already connected) — safe to call from every login/signup success
     * path and from app boot when a session is rehydrated from localStorage.
     */
    connectSockets(): void {
        this.socketIoService.connect()
        this.socketIoChatNamespaceService.connect()
    }

    /**
     * @param options.returnHere - come back to the current page after logging in again (an expired
     *   session), rather than a deliberate logout, where the next person shouldn't land on it.
     */
    logout(options: { returnHere?: boolean } = {}): void {
        const returnUrl = options.returnHere ? safeReturnUrl(this.router.url) : null

        // Release this device's push subscription first, while the session still works - otherwise
        // the next person on a shared phone gets this account's notifications. After an expired
        // session (returnHere) the backend would refuse the call (and its 401 would log out again),
        // so only the browser side is done then. Fetched lazily: PushNotificationService injects
        // CallerService, so it can't be a constructor dependency here.
        this.injector
            .get(PushNotificationService)
            .releaseThisDevice({ tellBackend: !options.returnHere })
            .then(() => this.endSession(returnUrl))
    }

    private endSession(returnUrl: string | null): void {
        localStorage.removeItem(CallerService.LOCAL_STORAGE_DATA_KEY)
        sessionStorage.removeItem(CallerService.SESSION_STORAGE_DATA_KEY)
        // The next person to log in on this device has their own saved items.
        this.store.dispatch(savedSalesLoaded({ ids: null }))
        this.store.dispatch(savedAccommodationsLoaded({ ids: null }))
        // ...and never sees, or sends, this person's unsent chat messages.
        this.store.dispatch(chatOutboxCleared())
        this.injector.get(PpaDirectoryService).clear() // lazily, like PushNotificationService: it injects CallerService

        // Both sockets are app-wide singletons that are now intentionally long-lived across
        // page navigation (see SocketEffects) — this is the one place they should actually be
        // disconnected, so a logged-out tab doesn't keep an authenticated-looking connection
        // open under the previous user's identity. Their listeners stay registered (destroy()
        // no longer strips them) so the next connectSockets() call just works.
        this.socketIoService.destroy()
        this.socketIoChatNamespaceService.destroy()

        /**
         * Using this.router.navigate(['']) or '/', if you're in the / home page, won't navigate since the url doesn't change.
         */
        this.router.navigate(['/login'], returnUrl ? { queryParams: { [RETURN_URL_QUERY_PARAM]: returnUrl } } : {})
    }

    /**
     * Where every login/signup lands: the page they were sent to log in from (`returnUrl`, set by
     * the auth guards - e.g. a link in an email), else the national chat, so a freshly logged-in
     * user's first screen already has something in it. Other visits to /messages start on the chat list.
     * @todo: '/dashboard' for agents
     */
    redirectAfterAuth(): void {
        const returnUrl = safeReturnUrl(this.router.parseUrl(this.router.url).queryParams[RETURN_URL_QUERY_PARAM])
        if (returnUrl) {
            this.router.navigateByUrl(returnUrl)
            return
        }
        this.router.navigate(['/messages'], {
            queryParams: { [MESSAGES_CHAT_QUERY_PARAM]: NATIONAL_CHAT_URL_VALUE },
        })
    }

    updateProfileBio(data: any) {
        console.log(
            'calling via',
            environment.baseurl + URLPaths.corpMemberProfileBioUpdate,
        )

        return this.http
            .post(
                environment.baseurl + URLPaths.corpMemberProfileBioUpdate,
                data,
                this.JSONHttpOptions,
            )
            .pipe(
                timeout(15000),
                // retry(3), // retry a failed request up to 3 times
                catchError(this.handleError), // then handle the error
            )
    }

    updateProfileOtherDetails(data: any) {
        console.log(
            'calling via',
            environment.baseurl + URLPaths.corpMemberProfileOtherDetailsUpdate,
        )

        return this.http
            .post(
                environment.baseurl +
                    URLPaths.corpMemberProfileOtherDetailsUpdate,
                data,
                this.JSONHttpOptions,
            )
            .pipe(
                timeout(15000),
                // retry(3), // retry a failed request up to 3 times
                catchError(this.handleError), // then handle the error
            )
    }

    updateRoomReadReceipts(data: { room: string }) {
        console.log(
            'calling via',
            environment.baseurl + URLPaths.updateRoomReadReceipts,
        )

        return this.http
            .post(
                environment.baseurl +
                    URLPaths.updateRoomReadReceipts,
                data,
                this.JSONHttpOptions,
            )
            .pipe(
                timeout(15000),
                catchError(this.handleError),
            )
    }

    updateProfilePpaDetails(data: any) {
        console.log(
            'calling via',
            environment.baseurl + URLPaths.corpMemberProfilePpaDetailsUpdate,
        )

        return this.http
            .post(
                environment.baseurl +
                    URLPaths.corpMemberProfilePpaDetailsUpdate,
                data,
                this.JSONHttpOptions,
            )
            .pipe(
                timeout(15000),
                // retry(3), // retry a failed request up to 3 times
                catchError(this.handleError), // then handle the error
            )
    }

    updateProfileServiceDetails(data: any) {
        console.log(
            'calling via',
            environment.baseurl +
                URLPaths.corpMemberProfileServiceDetailsUpdate,
        )

        return this.http
            .post(
                environment.baseurl +
                    URLPaths.corpMemberProfileServiceDetailsUpdate,
                data,
                this.JSONHttpOptions,
            )
            .pipe(
                timeout(15000),
                // retry(3), // retry a failed request up to 3 times
                catchError(this.handleError), // then handle the error
            )
    }

    createOnboardingWishlistAlert(data: any) {
        return this.http
            .post(
                environment.baseurl + URLPaths.corpMemberOnboardingWishlist,
                data,
                this.JSONHttpOptions,
            )
            .pipe(timeout(15000), catchError(this.handleError))
    }

    /** How many listings nearby already match a wishlist entry, without saving it. */
    previewOnboardingWishlistMatches(data: any) {
        return this.http
            .post(
                environment.baseurl + URLPaths.corpMemberOnboardingWishlistMatches,
                data,
                this.JSONHttpOptions,
            )
            .pipe(timeout(15000), catchError(this.handleError))
    }

    /**
     * POST /profile/onboarding - one endpoint for everything the checklist records except the
     * wishlist. Send only what changed; `data` in the response is what was saved.
     */
    private updateOnboarding(changes: { first_name?: string; state_code?: string; lga?: string; push_step_complete?: true; prompt_shown?: true; snooze?: true }) {
        return this.http
            .post(environment.baseurl + URLPaths.corpMemberOnboarding, changes, this.JSONHttpOptions)
            .pipe(timeout(15000), catchError(this.handleError))
    }

    /** The name step. (POST /profile can't do this: it needs the state code and LGA too.) */
    saveOnboardingFirstName(firstName: string) {
        return this.updateOnboarding({ first_name: firstName })
    }

    /**
     * The state code step, for Google sign-ups (they don't have one). The backend answers with a new
     * login token that carries the state code - kept like a login's - and the sockets reconnect with
     * it, so the base socket joins the state's room.
     */
    saveOnboardingStateCode(stateCode: string, lga: string) {
        return this.updateOnboarding({ state_code: stateCode, lga }).pipe(
            tap((res: any) => {
                if (res.body.token) {
                    sessionStorage.setItem(CallerService.SESSION_STORAGE_DATA_KEY, res.body.token)
                }
                this.store.dispatch(profileUpdateSuccess({ data: res.body.data }))
                this.socketIoService.destroy()
                this.socketIoChatNamespaceService.destroy()
                this.connectSockets()
            }),
        )
    }

    markOnboardingPushStepComplete() {
        return this.updateOnboarding({ push_step_complete: true })
    }

    /** The logged-in corp member, fresh from the backend (same shape as the login response). */
    getMyProfile() {
        return this.http
            .get(environment.baseurl + URLPaths.corpMemberProfileMe, this.JSONHttpOptions)
            .pipe(timeout(15000), catchError(this.handleError))
    }

    markOnboardingPromptShown() {
        return this.updateOnboarding({ prompt_shown: true })
    }

    /**
     * "Remind me later" (the checklist's ✕ or link, or the bar's button): hides the checklist and the
     * bar straight away - a snooze in the store, ONBOARDING_SNOOZE_HOURS long - says when it'll be
     * back, and saves the snooze in the background (the backend's time replaces ours when it
     * answers). It never waits on the request, or reports it failing: they asked for it to go away.
     * If it still can't be saved after two retries, the snooze lasts until the next page load.
     */
    remindAboutOnboardingLater(): void {
        const snoozedUntil = new Date(Date.now() + ONBOARDING_SNOOZE_HOURS * 3_600_000).toISOString()
        this.store.dispatch(profileUpdateSuccess({ data: { onboarding_snoozed_until: snoozedUntil } }))
        this.showNotification(snoozeMessage(snoozedUntil))

        this.snoozeOnboardingChecklist()
            .pipe(
                // timer, not delay: delay waits on Date.now(), which never moves if the clock is fixed (E2E).
                retryWhen((errors) => errors.pipe(
                    mergeMap((error, retries) => (retries >= 2 ? throwError(error) : timer(3000))),
                )),
            )
            .subscribe({ error: (err) => console.warn('Couldn\'t save the onboarding snooze', err) })
    }

    /** Saves the snooze; updates the store with the backend's time. Use remindAboutOnboardingLater(). */
    snoozeOnboardingChecklist() {
        return this.updateOnboarding({ snooze: true })
            .pipe(
                tap((res: any) => this.store.dispatch(profileUpdateSuccess({ data: res.body.data }))),
            )
    }

    listMyAlerts() {
        return this.http
            .get(environment.baseurl + URLPaths.corpMemberAlerts, this.JSONHttpOptions)
            .pipe(timeout(15000), catchError(this.handleError))
    }

    deleteAlert(id: number) {
        return this.http
            .delete(environment.baseurl + URLPaths.corpMemberAlert, { ...this.JSONHttpOptions, body: { id } })
            .pipe(timeout(15000), catchError(this.handleError))
    }

    // TODO: This should be like an side effect.
    updateProfile(data: any) {
        console.log(
            'calling via',
            environment.baseurl +
                URLPaths.corpMemberProfileUpdate,
        )

        return this.http
            .post(
                environment.baseurl +
                    URLPaths.corpMemberProfileUpdate,
                data,
                this.JSONHttpOptions,
            )
            .pipe(
                timeout(15000),
                // retry(3), // retry a failed request up to 3 times
                catchError(this.handleError), // then handle the error
            )
    }

    joinWaitList(data: any) {
        console.log(
            'signing up via',
            environment.baseurl + URLPaths.joinWaitList,
        )

        return this.http
            .post(
                environment.baseurl + URLPaths.joinWaitList,
                data,
                this.JSONHttpOptions,
            )
            .pipe(
                timeout(15000),
                // retry(3), // retry a failed request up to 3 times
                catchError(this.handleError), // then handle the error
            )
    }

    createNewAccommodationPost(data: FormData) {
        return this.http.post(
            environment.baseurl + URLPaths.createNewAccommodationPost,
            data,
            this.FormDataOptions
        )
    }

    createNewSalePost(data: FormData) {
        return this.http.post(
            environment.baseurl + URLPaths.createNewSalePost,
            data,
            this.FormDataOptions
        )
    }

    signUp(data: any) {
        console.log(
            'sign up up via',
            environment.baseurl + URLPaths.corpMemberSignUp,
        )

        return this.http
            .post(
                environment.baseurl + URLPaths.corpMemberSignUp,
                data,
                this.JSONHttpOptions,
            )
            .pipe(
                timeout(15000),
                // retry(3), // retry a failed request up to 3 times
                catchError(this.handleError), // then handle the error
            )
    }

    corpMemberLogIn(data: any) {
        console.log(
            'log in via',
            environment.baseurl + URLPaths.corpMemberLogIn,
        )

        return this.http
            .post(
                environment.baseurl + URLPaths.corpMemberLogIn,
                data,
                this.JSONHttpOptions,
            )
            .pipe(
                timeout(15000),
                // retry(3), // retry a failed request up to 3 times
                catchError(this.handleError), // we'll handle from the calling component
            )
    }

    corpMemberGoogleLogin(data: any) {
        console.log(
            'google login up via',
            environment.baseurl + URLPaths.corpMemberGoogleLogin,
        )

        return this.http
            .post(
                environment.baseurl + URLPaths.corpMemberGoogleLogin,
                data,
                this.JSONHttpOptions,
            )
            .pipe(
                timeout(15000),
                // retry(3), // retry a failed request up to 3 times
                catchError(this.handleError), // we'll handle from the calling component
            )
    }

    corpMemberGoogleSignUp(data: any) {
        console.log(
            'sign up via',
            environment.baseurl + URLPaths.corpMemberGoogleSignUp,
        )

        return this.http
            .post(
                environment.baseurl + URLPaths.corpMemberGoogleSignUp,
                data,
                this.JSONHttpOptions,
            )
            .pipe(
                timeout(15000),
                // retry(3), // retry a failed request up to 3 times
                catchError(this.handleError), // we'll handle from the calling component
            )
    }

    verifyEmail(token: string) {
        return this.http
            .post(
                environment.baseurl + URLPaths.verifyEmail,
                { token },
                this.JSONHttpOptions,
            )
            .pipe(
                timeout(15000),
                catchError(this.handleError), // we'll handle from the calling component
            )
    }

    snoozeVerificationReminder() {
        return this.http
            .post(environment.baseurl + URLPaths.snoozeVerificationReminder, {}, this.JSONHttpOptions)
            .pipe(timeout(15000), catchError(this.handleError))
    }

    resendVerificationEmail() {
        return this.http
            .post(
                environment.baseurl + URLPaths.resendVerificationEmail,
                {},
                this.JSONHttpOptions,
            )
            .pipe(
                timeout(15000),
                catchError(this.handleError), // we'll handle from the calling component
            )
    }

    forgotPassword(email: string) {
        return this.http
            .post(
                environment.baseurl + URLPaths.forgotPassword,
                { email },
                this.JSONHttpOptions,
            )
            .pipe(
                timeout(15000),
                catchError(this.handleError), // we'll handle from the calling component
            )
    }

    resetPassword(token: string, password: string) {
        return this.http
            .post(
                environment.baseurl + URLPaths.resetPassword,
                { token, password },
                this.JSONHttpOptions,
            )
            .pipe(
                timeout(15000),
                catchError(this.handleError), // we'll handle from the calling component
            )
    }

    // Reference data - genuinely static (seeded once on the backend), so it's
    // cached at the HTTP layer (see CacheInterceptor) rather than per-service-method:
    // that also fixes this being cached "per call site" when used from a
    // resolver vs a component, since the cache now lives below both.
    private readonly REFERENCE_DATA_CACHE_TTL_MS = 24 * 60 * 60 * 1000 // 24h

    fetchAllNigeriaStates() {
        return this.http.get(
            environment.baseurl + URLPaths.getNgStates,
            { ...this.JSONHttpOptions, context: new HttpContext().set(CACHE_TTL_MS, this.REFERENCE_DATA_CACHE_TTL_MS) }
        )
    }

    fetchAllNigeriaStatesAndLGAs() {
        return this.http.get(
            environment.baseurl + URLPaths.getNgStateAndLGAs,
            { ...this.JSONHttpOptions, context: new HttpContext().set(CACHE_TTL_MS, this.REFERENCE_DATA_CACHE_TTL_MS) }
        )
    }

    fetchNigeriaStateLGAs(stateId: number) {
        return this.http.get(
            environment.baseurl + URLPaths.getNgStateLGAs.replace(':state_id', `${stateId}`),
            { ...this.JSONHttpOptions, context: new HttpContext().set(CACHE_TTL_MS, this.REFERENCE_DATA_CACHE_TTL_MS) }
        )
    }

    /** Every PPA in the LGA, lean - for the PPA autocomplete. Use PpaDirectoryService, which caches it. */
    fetchLgaPpaAutocomplete(lgaId: string | number) {
        return this.http.get(
            environment.baseurl + URLPaths.getLgaPpaAutocomplete.replace(':lga_id', `${lgaId}`),
            this.JSONHttpOptions
        )
    }

    addNewPPA(data: FormData) {
        return this.http.post(
            environment.baseurl + URLPaths.createNewPPA,
            data,
            this.FormDataOptions
        )
    }

    updateProfilePicture(data: FormData) {
        return this.http.post(
            environment.baseurl + URLPaths.corpMemberProfilePhotoUpdate,
            data,
            this.FormDataOptions
        )
    }

    /**
     * NOT USING
     * TODO: we would need to maybe filter by some params later.
     * @returns 
     */
    /** One page of PPAs, filtered and sorted on the server. Responds with { ppas, total }. */
    getAllPPAs(query: { state_id?: number | null; lga_id?: number | null; sort?: string; limit?: number; offset?: number } = {}) {
        const params: Record<string, string> = {}
        Object.entries(query).forEach(([key, value]) => {
            if (value !== null && value !== undefined && value !== '') {
                params[key] = String(value)
            }
        })
        return this.http.get(environment.baseurl + URLPaths.getAllPPAs, { ...this.JSONHttpOptions, params })
    }

    // Matches the backend's own /all-items cache TTL (see corpers.online's
    // cacheRoute middleware) - no point caching fresher than the source does.
    private readonly ALL_ITEMS_CACHE_TTL_MS = 45 * 1000

    getAllItems() {
        return this.http.get(
            environment.baseurl + URLPaths.getAllItems,
            { ...this.JSONHttpOptions, context: new HttpContext().set(CACHE_TTL_MS, this.ALL_ITEMS_CACHE_TTL_MS) }
        )
    }

    getAllBookmarkedItems() {
        return this.http.get(
            environment.baseurl + URLPaths.getAllBookmarkedItems,
            this.JSONHttpOptions
        )
    }

    getAllPostedItems() {
        return this.http.get(
            environment.baseurl + URLPaths.getAllPostedItems,
            this.JSONHttpOptions
        )
    }

    getAllChats() {
        return this.http.get(
            environment.baseurl + URLPaths.getAllChats,
            this.JSONHttpOptions
        )
    }

    getOlderChatMessages(room: string, beforeId: number, limit = 50) {
        return this.http.get(
            environment.baseurl + URLPaths.getOlderChatMessages.replace(':room', room) + `?before_id=${beforeId}&limit=${limit}`,
            this.JSONHttpOptions
        )
    }

    bookmarkChatMessage(chatId: number) {
        return this.http.put(
            environment.baseurl + URLPaths.chatBookmark + chatId,
            null,
            this.JSONHttpOptions
        )
    }

    unbookmarkChatMessage(chatId: number) {
        return this.http.delete(
            environment.baseurl + URLPaths.chatBookmark + chatId,
            this.JSONHttpOptions
        )
    }

    getBookmarkedChatMessages() {
        return this.http.get(
            environment.baseurl + URLPaths.getBookmarkedChatMessages,
            this.JSONHttpOptions
        )
    }

    addNewPpaReview(data: FormData) {
        return this.http.post(
            environment.baseurl + URLPaths.createNewPpaReview,
            data,
            this.JSONHttpOptions
        )
    }

    searchPPAs(data: object) {
        return this.http.post(
            environment.baseurl + URLPaths.searchPPAs,
            data,
            this.JSONHttpOptions
        )
    }

    search(data: object) {
        return this.http.post(
            environment.baseurl + URLPaths.search,
            data,
            this.JSONHttpOptions
        )
    }

    /** 200 when saved, 202 when it already was - both mean it's saved now. */
    bookmarkSale(saleId: number) {
        return this.http.put(
            environment.baseurl + URLPaths.saleBookmark + saleId,
            null,
            this.JSONHttpOptions
        )
    }

    unBookmarkSale(saleId: number) {
        return this.http.delete(
            environment.baseurl + URLPaths.saleBookmark + saleId,
            this.JSONHttpOptions
        )
    }

    /** Public totals (houses, items for sale, PPAs, ...) - see LearnMoreService.stats$, which loads them once. */
    getPublicStats() {
        return this.http.get<{ data: PublicStats }>(environment.baseurl + URLPaths.publicStats, this.JSONHttpOptions)
    }

    getSavedAccommodationIds() {
        return this.http.get<{ data: number[] }>(environment.baseurl + URLPaths.accommodationBookmarks, this.JSONHttpOptions)
    }

    bookmarkAccommodation(accommodationId: number) {
        return this.http.put(environment.baseurl + URLPaths.accommodationBookmark + accommodationId, null, this.JSONHttpOptions)
    }

    unBookmarkAccommodation(accommodationId: number) {
        return this.http.delete(environment.baseurl + URLPaths.accommodationBookmark + accommodationId, this.JSONHttpOptions)
    }

    getSavedSaleIds() {
        return this.http.get<{ data: number[] }>(
            environment.baseurl + URLPaths.saleBookmarks,
            this.JSONHttpOptions
        )
    }

    /** Marks your sale sold (`sold: true`) or available again. */
    setSaleSold(saleId: number, sold: boolean) {
        return this.http.patch<{ data: { id: number, sold_at: string | null } }>(
            environment.baseurl + URLPaths.saleSold.replace(':id', String(saleId)),
            { sold },
            this.JSONHttpOptions
        )
    }

    getSaleConversationCount(saleId: number) {
        return this.http.get<{ data: { conversations: number } }>(
            environment.baseurl + URLPaths.saleConversations.replace(':id', String(saleId)),
            this.JSONHttpOptions
        )
    }

    getAccommodationConversationCount(accommodationId: number) {
        return this.http.get<{ data: { conversations: number } }>(
            environment.baseurl + URLPaths.accommodationConversations.replace(':id', String(accommodationId)),
            this.JSONHttpOptions
        )
    }

    bookmarkPpa(ppaId: number) {
        return this.http.put(
            environment.baseurl + URLPaths.ppaBookmark + ppaId,
            this.JSONHttpOptions
        )
    }

    unBookmarkPpa(ppaId: number) {
        return this.http.delete(
            environment.baseurl + URLPaths.ppaBookmark + ppaId,
            this.JSONHttpOptions
        )
    }

    likePpa(ppaId: number) {
        return this.http.put(
            environment.baseurl + URLPaths.ppaLike + ppaId,
            this.JSONHttpOptions
        )
    }

    unLikePpa(ppaId: number) {
        return this.http.delete(
            environment.baseurl + URLPaths.ppaLike + ppaId,
            this.JSONHttpOptions
        )
    }

    deletePostedItem(body: { id: number, type: string }) {
        return this.http.delete(
            environment.baseurl + URLPaths.deletePostedItem,
            {
                body,
                ...this.JSONHttpOptions
            }
        )
    }

    restorePostedItem(body: { id: number, type: 'sale' | 'accommodation' }) {
        return this.http.post<{ data: SaleType | AccommodationType }>(
            environment.baseurl + URLPaths.restorePostedItem,
            body,
            this.JSONHttpOptions
        )
    }

    /**
     * After deleting one of your posts: says so, with Undo. Undo restores it (the backend keeps a
     * deleted post for 14 days) and puts it back in post history and the feed.
     */
    offerUndoDelete(post: { id: number, type: 'sale' | 'accommodation' }, message: string) {
        this.showUndoNotification(message, () => {
            this.restorePostedItem(post).subscribe({
                next: (res) => {
                    if (res.body?.data) this.store.dispatch(postRestored({ post: res.body.data }))
                    this.showNotification(post.type === 'sale' ? 'Sale restored.' : 'Accommodation restored.')
                },
                error: (err) => this.showNotification(err?.error?.message ?? "Couldn't restore it. Please try again."),
            })
        })
    }

    searchCorpMembersForMention(query: string) {
        return this.http.get(
            environment.baseurl + URLPaths.searchCorpMembersForMention + '?q=' + encodeURIComponent(query),
            this.JSONHttpOptions
        ).pipe(
            timeout(15000),
            catchError(this.handleError),
        )
    }

    getCorpMemberProfile(body: any) {
        return this.http.get(
            environment.baseurl + URLPaths.getCorpMemberData.replace(':id', body?.id),
            this.JSONHttpOptions
        )
    }

    getPublicSale(id: string | null) {
        return this.http.get(
            environment.baseurl + URLPaths.getSaleById.replace(':id', id ?? ''),
            this.JSONHttpOptions
        )
    }

    getPublicAccommodation(id: string | null) {
        return this.http.get(
            environment.baseurl + URLPaths.getAccommodationById.replace(':id', id ?? ''),
            this.JSONHttpOptions
        )
    }

    getPublicPpa(id: string | null) {
        return this.http.get(
            environment.baseurl + URLPaths.getPpaById.replace(':id', id ?? ''),
            this.JSONHttpOptions
        )
    }

    /** The next page of a PPA's reviews: those before `before` (the last page's `next_cursor`). */
    getPpaReviews(id: string, before: number): Observable<ReviewPage> {
        return this.http.get<ReviewPage>(
            environment.baseurl + URLPaths.getPpaReviews.replace(':id', encodeURIComponent(id)),
            { params: { before } }
        )
    }

    getVapidPublicKey() {
        return this.http.get(
            environment.baseurl + URLPaths.vapidPublicKey,
            { ...this.JSONHttpOptions, context: new HttpContext().set(CACHE_TTL_MS, this.REFERENCE_DATA_CACHE_TTL_MS) }
        )
        .pipe(
            timeout(15000),
            catchError(this.handleError),
        )
    }

    savePushSubscription(data: any) {
        return this.http
            .post(
                environment.baseurl + URLPaths.savePushSubscription,
                data,
                this.JSONHttpOptions,
            )
            .pipe(
                timeout(15000),
                catchError(this.handleError),
            )
    }

    deletePushSubscription(body: { endpoint: string }) {
        return this.http.delete(
            environment.baseurl + URLPaths.deletePushSubscription,
            {
                body,
                ...this.JSONHttpOptions
            }
        )
        .pipe(
            timeout(15000),
            catchError(this.handleError),
        )
    }
}
